import React, { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import ReportApi, { ReportEntityType } from "../api/ReportApi";

// DSA art. 16 notice-and-action: "Probleem melden"-link + dialog. Mounted on
// public content pages (psychologist profile/booking, courses). Strings are
// deliberately Dutch-only (legal copy, NL-first product).
export default function ReportProblemDialog({ entityType, entityRef }: { entityType: ReportEntityType; entityRef?: string }) {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState(""); // honeypot — invisible, must stay empty
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [reference, setReference] = useState("");

  const close = () => {
    setOpen(false);
    // Keep the reference visible if reopened right away; reset the rest lazily.
    setError("");
  };

  const submit = async () => {
    if (!description.trim()) {
      setError("Omschrijf kort wat er mis is.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await ReportApi.create({
        entityType,
        entityRef,
        description: description.trim().slice(0, 5000),
        reporterEmail: email.trim() || undefined,
        website,
      });
      setReference(res.reference);
      setDescription("");
      setEmail("");
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.response?.data?.message || "Er ging iets mis. Probeer het later opnieuw.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Link
        component="button"
        type="button"
        onClick={() => setOpen(true)}
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: ".85rem", color: "text.secondary" }}
      >
        <FlagOutlinedIcon sx={{ fontSize: "1rem" }} /> Probleem melden
      </Link>

      <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
        <DialogTitle>Probleem melden</DialogTitle>
        <DialogContent>
          {reference ? (
            <Alert severity="success">
              Bedankt voor je melding. Je referentienummer is <strong>{reference}</strong>.
              {" "}Als je een e-mailadres opgaf, ontvang je een ontvangstbevestiging en later onze beslissing.
            </Alert>
          ) : (
            <>
              <Typography sx={{ fontSize: ".9rem", color: "text.secondary", mb: 1.5 }}>
                Zie je op deze pagina inhoud die onjuist, misleidend of onrechtmatig is? Meld het ons — we bekijken elke melding zorgvuldig.
              </Typography>
              <TextField
                label="Wat is er mis?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                minRows={4}
                fullWidth
                inputProps={{ maxLength: 5000 }}
                sx={{ mb: 1.5 }}
              />
              <TextField
                label="Je e-mailadres (optioneel)"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                helperText="Alleen nodig als je een ontvangstbevestiging en onze beslissing wilt ontvangen."
                fullWidth
              />
              {/* Honeypot: invisible for humans, bots fill it in. */}
              <input
                type="text"
                name="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
              />
              {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Sluiten</Button>
          {!reference && (
            <Button variant="contained" onClick={submit} disabled={busy || !description.trim()}>
              {busy ? "Versturen…" : "Melding versturen"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
